import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './participanteformpage.module.css';

// API
import { useEmpresaService } from '../../services/empresaServices';
import { useTurmaService } from '../../services/turmaServices';
import { useParticipanteService } from '../../services/participanteServices';

const COUNTRY_OPTIONS = [
  { iso: 'BR', ddi: '55', name: 'Brasil', placeholder: '(11) 91234-5678', minLength: 11, maxLength: 11 },
  { iso: 'US', ddi: '1', name: 'Estados Unidos', placeholder: '201 555 0123', minLength: 10, maxLength: 10 },
  { iso: 'CA', ddi: '1', name: 'Canada', placeholder: '416 555 0123', minLength: 10, maxLength: 10 },
  { iso: 'PT', ddi: '351', name: 'Portugal', placeholder: '912 345 678', minLength: 9, maxLength: 9 },
  { iso: 'GB', ddi: '44', name: 'Reino Unido', placeholder: '7123 456789', minLength: 9, maxLength: 11 },
  { iso: 'ES', ddi: '34', name: 'Espanha', placeholder: '612 345 678', minLength: 9, maxLength: 9 },
  { iso: 'FR', ddi: '33', name: 'Franca', placeholder: '6 12 34 56 78', minLength: 9, maxLength: 9 },
  { iso: 'DE', ddi: '49', name: 'Alemanha', placeholder: '1512 3456789', minLength: 7, maxLength: 11 },
  { iso: 'IT', ddi: '39', name: 'Italia', placeholder: '312 345 6789', minLength: 8, maxLength: 11 },
  { iso: 'JP', ddi: '81', name: 'Japao', placeholder: '90 1234 5678', minLength: 9, maxLength: 10 },
  { iso: 'MX', ddi: '52', name: 'Mexico', placeholder: '55 1234 5678', minLength: 10, maxLength: 10 },
  { iso: 'AR', ddi: '54', name: 'Argentina', placeholder: '11 2345 6789', minLength: 10, maxLength: 11 },
  { iso: 'CL', ddi: '56', name: 'Chile', placeholder: '9 1234 5678', minLength: 9, maxLength: 9 },
  { iso: 'CO', ddi: '57', name: 'Colombia', placeholder: '312 345 6789', minLength: 10, maxLength: 10 },
  { iso: 'PE', ddi: '51', name: 'Peru', placeholder: '912 345 678', minLength: 9, maxLength: 9 },
  { iso: 'AU', ddi: '61', name: 'Australia', placeholder: '412 345 678', minLength: 9, maxLength: 9 },
  { iso: 'NZ', ddi: '64', name: 'Nova Zelandia', placeholder: '21 234 5678', minLength: 8, maxLength: 10 },
  { iso: 'IE', ddi: '353', name: 'Irlanda', placeholder: '85 123 4567', minLength: 9, maxLength: 9 },
  { iso: 'NL', ddi: '31', name: 'Holanda', placeholder: '6 1234 5678', minLength: 9, maxLength: 9 },
  { iso: 'BE', ddi: '32', name: 'Belgica', placeholder: '470 12 34 56', minLength: 9, maxLength: 9 },
  { iso: 'CH', ddi: '41', name: 'Suica', placeholder: '79 123 45 67', minLength: 9, maxLength: 9 },
  { iso: 'AT', ddi: '43', name: 'Austria', placeholder: '664 1234567', minLength: 7, maxLength: 11 },
  { iso: 'SE', ddi: '46', name: 'Suecia', placeholder: '70 123 45 67', minLength: 7, maxLength: 10 },
  { iso: 'NO', ddi: '47', name: 'Noruega', placeholder: '412 34 567', minLength: 8, maxLength: 8 },
  { iso: 'DK', ddi: '45', name: 'Dinamarca', placeholder: '20 12 34 56', minLength: 8, maxLength: 8 },
  { iso: 'FI', ddi: '358', name: 'Finlandia', placeholder: '40 123 4567', minLength: 8, maxLength: 10 },
  { iso: 'PL', ddi: '48', name: 'Polonia', placeholder: '512 345 678', minLength: 9, maxLength: 9 },
  { iso: 'CZ', ddi: '420', name: 'Republica Checa', placeholder: '601 123 456', minLength: 9, maxLength: 9 },
  { iso: 'HU', ddi: '36', name: 'Hungria', placeholder: '20 123 4567', minLength: 9, maxLength: 9 },
  { iso: 'GR', ddi: '30', name: 'Grecia', placeholder: '691 234 5678', minLength: 10, maxLength: 10 },
  { iso: 'TR', ddi: '90', name: 'Turquia', placeholder: '532 123 4567', minLength: 10, maxLength: 10 },
  { iso: 'IL', ddi: '972', name: 'Israel', placeholder: '52 123 4567', minLength: 9, maxLength: 9 },
  { iso: 'AE', ddi: '971', name: 'Emirados Arabes', placeholder: '50 123 4567', minLength: 9, maxLength: 9 },
  { iso: 'SA', ddi: '966', name: 'Arabia Saudita', placeholder: '50 123 4567', minLength: 9, maxLength: 9 },
  { iso: 'ZA', ddi: '27', name: 'Africa do Sul', placeholder: '71 234 5678', minLength: 9, maxLength: 9 },
  { iso: 'EG', ddi: '20', name: 'Egito', placeholder: '100 123 4567', minLength: 10, maxLength: 10 },
  { iso: 'IN', ddi: '91', name: 'India', placeholder: '91234 56789', minLength: 10, maxLength: 10 },
  { iso: 'PK', ddi: '92', name: 'Paquistao', placeholder: '301 2345678', minLength: 10, maxLength: 10 },
  { iso: 'BD', ddi: '880', name: 'Bangladesh', placeholder: '1712 345678', minLength: 10, maxLength: 10 },
  { iso: 'CN', ddi: '86', name: 'China', placeholder: '13 1234 5678', minLength: 11, maxLength: 11 },
  { iso: 'KR', ddi: '82', name: 'Coreia do Sul', placeholder: '10 1234 5678', minLength: 9, maxLength: 10 },
  { iso: 'HK', ddi: '852', name: 'Hong Kong', placeholder: '9123 4567', minLength: 8, maxLength: 8 },
  { iso: 'SG', ddi: '65', name: 'Singapura', placeholder: '8123 4567', minLength: 8, maxLength: 8 },
  { iso: 'MY', ddi: '60', name: 'Malasia', placeholder: '12 345 6789', minLength: 9, maxLength: 10 },
  { iso: 'TH', ddi: '66', name: 'Tailandia', placeholder: '81 234 5678', minLength: 9, maxLength: 9 },
  { iso: 'VN', ddi: '84', name: 'Vietna', placeholder: '91 234 5678', minLength: 9, maxLength: 10 },
  { iso: 'PH', ddi: '63', name: 'Filipinas', placeholder: '917 123 4567', minLength: 10, maxLength: 10 },
  { iso: 'ID', ddi: '62', name: 'Indonesia', placeholder: '812 3456 7890', minLength: 9, maxLength: 12 },
];

function ParticipanteFormPage() {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idEmpresa, idTurma, idParticipante } = useParams();
  const { pathname } = useLocation();

  const { obterEmpresaPorId } = useEmpresaService();
  const { obterTurmaPorId } = useTurmaService();
  const { obterParticipantePorId, obterParticipantePorCelular, criarParticipante, atualizarParticipante } = useParticipanteService();

  const isEditing = !!idParticipante && pathname.includes('atualizar');

  const [empresa, setEmpresa] = useState(null);
  const [turma, setTurma] = useState(null);

  const countries = useMemo(() => COUNTRY_OPTIONS, []);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [countryQuery, setCountryQuery] = useState(countries[0].iso);

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    celular: '',
    senha: ''
  });

  const aplicarMascaraTelefone = (valor, pais) => {
    const numeros = valor.replace(/\D/g, '');

    if (pais.iso !== 'BR') {
      return numeros.slice(0, pais.maxLength || 14);
    }

    const limpo = numeros.slice(0, 11);
    const parte1 = limpo.slice(0, 2);
    const parte2 = limpo.slice(2, 7);
    const parte3 = limpo.slice(7, 11);

    if (limpo.length <= 2) return parte1 ? `(${parte1}` : '';
    if (limpo.length <= 7) return `(${parte1}) ${parte2}`;
    return `(${parte1}) ${parte2}-${parte3}`;
  };

  const normalizeCountryValue = (value) => value.toLowerCase().replace(/[^a-z]/g, '');

  const findCountryMatch = (value) => {
    const normalized = normalizeCountryValue(value);
    if (!normalized) return null;

    return countries.find((pais) => {
      const iso = pais.iso.toLowerCase();
      const nome = normalizeCountryValue(pais.name);
      return normalized === iso || nome.startsWith(normalized);
    });
  };

  const splitPhoneByCountry = (rawValue) => {
    const digits = String(rawValue || '').replace(/\D/g, '');
    const fallbackCountry = countries[0];

    if (!digits) {
      return { country: fallbackCountry, localNumber: '' };
    }

    let bestMatch = null;

    countries.forEach((pais) => {
      if (!digits.startsWith(pais.ddi)) return;

      const local = digits.slice(pais.ddi.length);
      const min = pais.minLength || 6;
      const max = pais.maxLength || 14;

      if (local.length >= min && local.length <= max) {
        if (!bestMatch || pais.ddi.length > bestMatch.country.ddi.length) {
          bestMatch = { country: pais, localNumber: local };
        }
      }
    });

    if (bestMatch) return bestMatch;

    if (digits.length === 11) {
      const br = countries.find((pais) => pais.iso === 'BR') || fallbackCountry;
      return { country: br, localNumber: digits };
    }

    return {
      country: fallbackCountry,
      localNumber: digits.slice(0, fallbackCountry.maxLength || 14)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'celular') {
      setFormData((prev) => ({
        ...prev,
        celular: aplicarMascaraTelefone(value, selectedCountry)
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryQueryChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCountryQuery(value);
    const match = findCountryMatch(value);
    if (match) {
      setSelectedCountry(match);
      setCountryQuery(match.iso);
      setFormData((prev) => ({
        ...prev,
        celular: aplicarMascaraTelefone(prev.celular, match)
      }));
    }
  };

  const handleCountryBlur = () => {
    setCountryQuery(selectedCountry.iso);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    showLoader(isEditing ? 'Atualizando participante' : 'Criando participante');

    const celularSemMascara = formData.celular.replace(/\D/g, '');
    const min = selectedCountry.minLength || 6;
    const max = selectedCountry.maxLength || 14;

    if (celularSemMascara.length < min || celularSemMascara.length > max) {
      showToast('O telefone esta incompleto para o pais selecionado.', 3000, 'error');
      hideLoader();
      return;
    }

    const celularComDDI = `${selectedCountry.ddi}${celularSemMascara}`;

    if (!isEditing) {
      const celularCheck = await obterParticipantePorCelular(celularComDDI);
      if (celularCheck.success && celularCheck.data) {
        showToast('Ja existe um participante com esse celular.', 3000, 'error');
        hideLoader();
        return;
      }
    }

    const participanteJson = {
      ...formData,
      celular: celularComDDI,
      loginPor: 'celular',
      idTurma: idTurma,
    };

    if (isEditing) participanteJson.id = idParticipante;

    const response = isEditing
      ? await atualizarParticipante(participanteJson)
      : await criarParticipante(participanteJson);

    if (response.success) {
      showToast(
        isEditing ? 'Participante atualizado com sucesso!' : 'Participante criado com sucesso!',
        3000,
        'success'
      );
      navigate(`/empresas/${idEmpresa}/${idTurma}`);
    } else {
      showToast(
        isEditing ? 'Erro ao atualizar participante.' : 'Erro ao criar participante.',
        3000,
        'error'
      );
      if (response.message?.toLowerCase().includes('token')) logout();
    }

    hideLoader();
  };

  useEffect(() => {
    const carregarDados = async () => {
      showLoader('Carregando dados');

      const empresaResp = await obterEmpresaPorId(idEmpresa);
      if (empresaResp.success) {
        setEmpresa(empresaResp.data);
      } else {
        showToast('Erro ao carregar empresa', 3000, 'error');
        if (empresaResp.message?.toLowerCase().includes('token')) logout();
        hideLoader();
        return;
      }

      const turmaResp = await obterTurmaPorId(idTurma);
      if (turmaResp.success) {
        setTurma(turmaResp.data);
      } else {
        showToast('Erro ao carregar turma', 3000, 'error');
        if (turmaResp.message?.toLowerCase().includes('token')) logout();
        hideLoader();
        return;
      }

      if (isEditing) {
        const participanteResp = await obterParticipantePorId(idParticipante);
        if (participanteResp.success) {
          const p = participanteResp.data;
          const { country, localNumber } = splitPhoneByCountry(p.celular || '');

          setSelectedCountry(country);
          setCountryQuery(country.iso);

          setFormData({
            nome: p.nome || '',
            email: p.email || '',
            celular: aplicarMascaraTelefone(localNumber, country),
            senha: ''
          });
        } else {
          showToast('Erro ao carregar participante', 3000, 'error');
          if (participanteResp.message?.toLowerCase().includes('token')) logout();
        }
      }

      hideLoader();
    };

    if (idEmpresa && idTurma) carregarDados();
  }, [idEmpresa, idTurma, idParticipante, isEditing]);

  const breadcrumbIds = [];
  if (empresa) breadcrumbIds.push({ id: idEmpresa, label: empresa.nome, place: 1 });
  if (turma) breadcrumbIds.push({ id: idTurma, label: turma.nome, place: 2 });
  if (isEditing && formData.nome) breadcrumbIds.push({ id: idParticipante, label: formData.nome, place: 3 });

  return (
    <div className={styles.ParticipanteFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbIds} />
        <h1 className={styles.title}>{isEditing ? 'Editar Participante' : 'Cadastro de Participante'}</h1>
        <FriendlyPhrase page={isEditing ? 'atualizarParticipante' : 'adicionarParticipante'} />
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario}>
        <label>Nome:
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
          />
        </label>

        <label>E-mail:
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </label>

        <label>Celular:
          <div className={styles.phoneRow}>
            <span className={styles.ddiLabel} id="country-iso-label">Sigla do pais</span>
            <input
              id="country-iso"
              className={styles.countryInput}
              value={countryQuery}
              onChange={handleCountryQueryChange}
              onBlur={handleCountryBlur}
              list="country-iso-list"
              placeholder="BR"
              autoComplete="off"
              inputMode="text"
              aria-labelledby="country-iso-label"
            />
            <datalist id="country-iso-list">
              {countries.map((pais) => (
                <option key={pais.iso} value={pais.iso} />
              ))}
            </datalist>

            <input
              id="celular-input"
              type="tel"
              name="celular"
              value={formData.celular}
              onChange={handleChange}
              required
              placeholder={selectedCountry.placeholder}
              inputMode="tel"
            />
          </div>
        </label>

        <label>Senha:
          <input
            type="password"
            name="senha"
            value={formData.senha}
            onChange={handleChange}
            placeholder={isEditing ? 'A senha antiga nao pode ser exibida' : ''}
          />
        </label>

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            {isEditing ? 'Atualizar Participante' : 'Salvar Participante'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ParticipanteFormPage;
